import React, { createContext, useState, useEffect, useContext, useCallback, useRef } from 'react';
import { API_BASE_URL } from '../config';

const PriceContext = createContext(null);

export const PriceProvider = ({ children }) => {
    const [priceMap, setPriceMap] = useState(new Map());
    const [loading, setLoading] = useState(true);

    const fetchInFlightRef = useRef(false);
    const pendingWsRefreshRef = useRef(false);
    const lastWsRefreshAtRef = useRef(0);
    const lastAppliedPricingVersionRef = useRef(0);
    const lastSignaledPricingVersionRef = useRef(0);

    const fetchPrices = useCallback(async ({ reason = 'manual' } = {}) => {
        const isWsTrigger = reason.startsWith('ws:');
        if (fetchInFlightRef.current) {
            if (isWsTrigger) pendingWsRefreshRef.current = true;
            return;
        }

        if (isWsTrigger) {
            const now = Date.now();
            // Burst guard for connect/reconnect event storms.
            if (now - lastWsRefreshAtRef.current < 750) return;
            lastWsRefreshAtRef.current = now;
        }

        const token = localStorage.getItem('authToken');
        if (!token) {
            setLoading(false);
            return;
        }

        fetchInFlightRef.current = true;
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/pricing/all`, {
                headers: { Authorization: `Bearer ${token}` },
                credentials: 'include'
            });
            if (!res.ok) throw new Error('Failed to fetch prices');

            const data = await res.json();
            const newPriceMap = new Map(Object.entries(data?.prices || {}));
            setPriceMap(newPriceMap);

            const incomingVersion = Number(data?.meta?.pricingVersion || 0);
            if (incomingVersion > 0) {
                lastAppliedPricingVersionRef.current = Math.max(lastAppliedPricingVersionRef.current, incomingVersion);
            }
        } catch (error) {
            console.error('Failed to fetch price map:', error);
        } finally {
            fetchInFlightRef.current = false;
            setLoading(false);
            if (pendingWsRefreshRef.current) {
                pendingWsRefreshRef.current = false;
                fetchPrices({ reason: 'ws:queued' });
            }
        }
    }, []);

    useEffect(() => {
        fetchPrices({ reason: 'bootstrap' });
    }, [fetchPrices]);

    useEffect(() => {
        const handleRealtimePricingRefresh = (evt) => {
            const incomingVersion = Number(evt?.detail?.event?.pricingVersion || 0);
            if (incomingVersion > 0) {
                if (incomingVersion <= lastSignaledPricingVersionRef.current) return;
                if (incomingVersion <= lastAppliedPricingVersionRef.current) return;
                lastSignaledPricingVersionRef.current = incomingVersion;
            }
            fetchPrices({ reason: 'ws:pricing-updated' });
        };

        const handleRealtimeForceRefetch = () => {
            fetchPrices({ reason: 'ws:force-refetch' });
        };

        window.addEventListener('realtime:pricing-updated', handleRealtimePricingRefresh);
        window.addEventListener('realtime:force-refetch', handleRealtimeForceRefetch);
        return () => {
            window.removeEventListener('realtime:pricing-updated', handleRealtimePricingRefresh);
            window.removeEventListener('realtime:force-refetch', handleRealtimeForceRefetch);
        };
    }, [fetchPrices]);

    const value = {
        priceMap,
        refetchPrices: fetchPrices,
        loading
    };

    return (
        <PriceContext.Provider value={value}>
            {children}
        </PriceContext.Provider>
    );
};

export const usePrices = () => {
    const context = useContext(PriceContext);
    if (context === null) {
        throw new Error('usePrices must be used within a PriceProvider');
    }
    return context;
};
